/**
 * Module visibility — which sidebar modules each role can see, and which of them
 * are tagged "New".
 *
 * Every (module, role) pair is its own visibility switch, so a module can be on
 * for buyers and off for advisors and admins at the same time. A role only gets
 * a switch for modules its sidebar actually carries; the rest of the row is
 * blank rather than showing a toggle that would control nothing.
 *
 * The New tag is the last column and is deliberately NOT per role — a module is
 * new or it isn't. Switching it on puts a green "New" pill beside that module in
 * every sidebar that carries it.
 *
 * Core navigation — each role's home/listings surface, Settings, Support — is
 * deliberately absent from this page. Switching those off would strand a role
 * with no route back to their own account.
 */
import { useEffect, useState } from "react";
import { Loader2, ToggleLeft } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface ModuleRow {
  key: string;
  label: string;
  description: string;
  /** Only the roles whose sidebar carries this module. */
  roles: Record<string, boolean>;
  /** The "New" pill in the sidebar — one flag for the module, not per role. */
  is_new: boolean;
}

interface ModulesResponse {
  role_labels: Record<string, string>;
  modules: ModuleRow[];
}

function Switch({
  on,
  busy,
  label,
  onToggle,
}: {
  on: boolean;
  busy: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={busy}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 disabled:opacity-50 ${
        on ? "bg-primary" : "bg-muted-foreground/30"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
          on ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function SuperAdminModulesPage() {
  const [data, setData] = useState<ModulesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    superAdminApi
      .get<ModulesResponse>("/super-admin/modules")
      .then((d) => { if (!stale) setData(d); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : "Could not load modules."); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, []);

  const toggle = async (key: string, role: string, next: boolean) => {
    const id = `${key}:${role}`;
    setSavingKey(id);
    setError(null);
    try {
      setData(await superAdminApi.patch<ModulesResponse>("/super-admin/modules", { key, role, enabled: next }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the change.");
    } finally {
      setSavingKey(null);
    }
  };

  const toggleNew = async (key: string, next: boolean) => {
    setSavingKey(`${key}:__new__`);
    setError(null);
    try {
      setData(await superAdminApi.patch<ModulesResponse>("/super-admin/modules/new-tag", { key, is_new: next }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the change.");
    } finally {
      setSavingKey(null);
    }
  };

  const roles = data ? Object.keys(data.role_labels) : [];

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Platform"
        icon={ToggleLeft}
        title="Module"
        titleAccent="Visibility"
        description="Switch a module on or off per role, and tag it New. A module can be visible to buyers while hidden from BL advisors and admins — each cell is its own switch. The New tag applies to every role that can see the module."
      />

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}

      <section className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading modules…
          </div>
        ) : !data ? null : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border/70 bg-muted/40 text-left text-[0.62rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Module</th>
                  {roles.map((r) => (
                    <th key={r} className="px-4 py-3 text-center">{data.role_labels[r]}</th>
                  ))}
                  {/* Divider before the New column — it is a different kind of
                      switch from the per-role visibility ones to its left. */}
                  <th className="border-l border-border/70 px-4 py-3 text-center">New tag</th>
                </tr>
              </thead>
              <tbody>
                {data.modules.map((m) => (
                  <tr key={m.key} className="border-b border-border/50 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-foreground">{m.label}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{m.description}</div>
                    </td>
                    {roles.map((role) => {
                      const applicable = role in m.roles;
                      const on = m.roles[role];
                      const busy = savingKey === `${m.key}:${role}`;
                      return (
                        <td key={role} className="px-4 py-3 text-center">
                          {!applicable ? (
                            // This role's sidebar never had the module — a switch
                            // here would control nothing, so show why instead.
                            <span className="text-xs text-muted-foreground/50" title="Not in this role's sidebar">
                              n/a
                            </span>
                          ) : (
                            <Switch
                              on={on}
                              busy={busy}
                              label={`${m.label} for ${data.role_labels[role]}`}
                              onToggle={() => void toggle(m.key, role, !on)}
                            />
                          )}
                        </td>
                      );
                    })}
                    <td className="border-l border-border/70 px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          on={m.is_new}
                          busy={savingKey === `${m.key}:__new__`}
                          label={`Tag ${m.label} as New`}
                          onToggle={() => void toggleNew(m.key, !m.is_new)}
                        />
                        {/* Preview of the actual sidebar pill, so the switch's
                            effect is visible without leaving the console. */}
                        {m.is_new && (
                          <span className="rounded-full border border-emerald-600 bg-emerald-600 px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.08em] text-white">
                            New
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs leading-6 text-muted-foreground">
        Changes reach a user on their next page load. Hiding a module removes it from
        the sidebar only — anyone with the direct URL still reaches the page, so treat
        this as curating the navigation, not as an access control. A New tag is skipped
        for the few entries that already carry their own badge (AI Deal Matching shows
        &ldquo;AI&rdquo;, Upcoming Modules shows &ldquo;Soon&rdquo;), since an entry can
        only wear one pill.
      </p>
    </main>
  );
}
